/**
 * CMS Section Editor
 * Extracted from PageContentManager.jsx as part of P2 tech-debt refactor.
 *
 * Renders the appropriate editor UI for a given section key (hero, stats,
 * timeline, destination_hero, intakes, etc.). Each case in the switch is
 * a self-contained editor for that section's data shape.
 *
 * Public API: <SectionEditor sectionKey={string} content={any} onChange={fn} />
 *
 * The "Initialize Section" button (shown when content is null/undefined)
 * uses getDefaultContentForSection from ./pageConfig.js.
 */
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { AlertCircle, Plus, Trash2 } from 'lucide-react';
import { SECTION_LABELS, getDefaultContentForSection } from './pageConfig';

// Generic field editor component
const FieldEditor = ({ label, value, onChange, type = 'text', rows = 3, placeholder = '' }) => {
  if (type === 'textarea') {
    return (
      <div className="space-y-2">
        <Label className="text-sm font-medium">{label}</Label>
        <Textarea
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          rows={rows}
          placeholder={placeholder}
          className="resize-none"
        />
      </div>
    );
  }
  
  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">{label}</Label>
      <Input
        type={type}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </div>
  );
};

// Section editor for different content types
const SectionEditor = ({ sectionKey, content, onChange }) => {
  // If no content, show initialization option
  if (!content) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <AlertCircle className="w-12 h-12 text-amber-500 mb-4" />
        <h4 className="text-lg font-medium text-ux-charcoal mb-2">No content yet</h4>
        <p className="text-ux-charcoal/60 mb-4">This section hasn't been initialized. Click below to add content.</p>
        <Button 
          onClick={() => onChange(getDefaultContentForSection(sectionKey))}
          className="bg-ux-cyan hover:bg-ux-cyan/90"
        >
          <Plus className="w-4 h-4 mr-2" />
          Initialize Section
        </Button>
      </div>
    );
  }
  
  // Render appropriate editor based on section key
  const renderEditor = () => {
    switch (sectionKey) {
      case 'hero':
        return (
          <div className="space-y-4">
            <FieldEditor
              label="Badge Text"
              value={content.badge_text}
              onChange={(v) => onChange({ ...content, badge_text: v })}
              placeholder="e.g., Trusted by 30,000+ Students"
            />
            <FieldEditor
              label="Main Title"
              value={content.title}
              onChange={(v) => onChange({ ...content, title: v })}
              placeholder="e.g., DREAM BIG."
            />
            <FieldEditor
              label="Title Highlight (Gradient Text)"
              value={content.title_highlight}
              onChange={(v) => onChange({ ...content, title_highlight: v })}
              placeholder="e.g., GO FAR."
            />
            <FieldEditor
              label="Subtitle"
              value={content.subtitle}
              onChange={(v) => onChange({ ...content, subtitle: v })}
              type="textarea"
              rows={3}
              placeholder="Supporting text under the main headline"
            />
            <div className="grid grid-cols-2 gap-4">
              <FieldEditor
                label="Primary CTA Text"
                value={content.cta_primary_text}
                onChange={(v) => onChange({ ...content, cta_primary_text: v })}
              />
              <FieldEditor
                label="Primary CTA Link"
                value={content.cta_primary_link}
                onChange={(v) => onChange({ ...content, cta_primary_link: v })}
              />
            </div>
          </div>
        );
        
      case 'seo':
        return (
          <div className="space-y-4">
            <FieldEditor
              label="Page Title"
              value={content.title}
              onChange={(v) => onChange({ ...content, title: v })}
              placeholder="SEO title for browser tab"
            />
            <FieldEditor
              label="Meta Description"
              value={content.description}
              onChange={(v) => onChange({ ...content, description: v })}
              type="textarea"
              rows={3}
              placeholder="SEO description (150-160 characters recommended)"
            />
            <FieldEditor
              label="Keywords"
              value={content.keywords}
              onChange={(v) => onChange({ ...content, keywords: v })}
              type="textarea"
              rows={2}
              placeholder="Comma-separated keywords"
            />
          </div>
        );
        
      case 'cta':
        return (
          <div className="space-y-4">
            <FieldEditor
              label="CTA Title"
              value={content.title}
              onChange={(v) => onChange({ ...content, title: v })}
            />
            <FieldEditor
              label="CTA Subtitle"
              value={content.subtitle}
              onChange={(v) => onChange({ ...content, subtitle: v })}
              type="textarea"
              rows={2}
            />
            <div className="grid grid-cols-2 gap-4">
              <FieldEditor
                label="Primary Button Text"
                value={content.cta_primary_text}
                onChange={(v) => onChange({ ...content, cta_primary_text: v })}
              />
              <FieldEditor
                label="Primary Button Link"
                value={content.cta_primary_link}
                onChange={(v) => onChange({ ...content, cta_primary_link: v })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <FieldEditor
                label="Secondary Button Text"
                value={content.cta_secondary_text}
                onChange={(v) => onChange({ ...content, cta_secondary_text: v })}
              />
              <FieldEditor
                label="Secondary Button Link"
                value={content.cta_secondary_link}
                onChange={(v) => onChange({ ...content, cta_secondary_link: v })}
              />
            </div>
          </div>
        );
        
      case 'story':
        return (
          <div className="space-y-4">
            <FieldEditor
              label="Section Badge"
              value={content.badge}
              onChange={(v) => onChange({ ...content, badge: v })}
            />
            <FieldEditor
              label="Title"
              value={content.title}
              onChange={(v) => onChange({ ...content, title: v })}
            />
            <FieldEditor
              label="Title Highlight"
              value={content.title_highlight}
              onChange={(v) => onChange({ ...content, title_highlight: v })}
            />
            <div className="space-y-2">
              <Label className="text-sm font-medium">Story Paragraphs</Label>
              {(content.paragraphs || []).map((para, idx) => (
                <div key={idx} className="flex gap-2">
                  <Textarea
                    value={para}
                    onChange={(e) => {
                      const newParagraphs = [...(content.paragraphs || [])];
                      newParagraphs[idx] = e.target.value;
                      onChange({ ...content, paragraphs: newParagraphs });
                    }}
                    rows={3}
                    className="flex-1"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const newParagraphs = content.paragraphs.filter((_, i) => i !== idx);
                      onChange({ ...content, paragraphs: newParagraphs });
                    }}
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const newParagraphs = [...(content.paragraphs || []), ''];
                  onChange({ ...content, paragraphs: newParagraphs });
                }}
              >
                <Plus className="w-4 h-4 mr-2" /> Add Paragraph
              </Button>
            </div>
          </div>
        );
        
      case 'stats':
      case 'global_stats':
      case 'company_stats':
        return (
          <div className="space-y-4">
            <Label className="text-sm font-medium">Statistics</Label>
            <div className="space-y-3">
              {(Array.isArray(content) ? content : []).map((stat, idx) => (
                <Card key={idx} className="p-3">
                  <div className="grid grid-cols-3 gap-2">
                    <Input
                      placeholder="Label"
                      value={stat.label || ''}
                      onChange={(e) => {
                        const newStats = [...content];
                        newStats[idx] = { ...newStats[idx], label: e.target.value };
                        onChange(newStats);
                      }}
                    />
                    <Input
                      placeholder="Value"
                      value={stat.value || ''}
                      onChange={(e) => {
                        const newStats = [...content];
                        newStats[idx] = { ...newStats[idx], value: e.target.value };
                        onChange(newStats);
                      }}
                    />
                    <div className="flex gap-2">
                      <Input
                        placeholder="Icon"
                        value={stat.icon || ''}
                        onChange={(e) => {
                          const newStats = [...content];
                          newStats[idx] = { ...newStats[idx], icon: e.target.value };
                          onChange(newStats);
                        }}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const newStats = content.filter((_, i) => i !== idx);
                          onChange(newStats);
                        }}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const newStats = [...(Array.isArray(content) ? content : []), { label: '', value: '', icon: '' }];
                  onChange(newStats);
                }}
              >
                <Plus className="w-4 h-4 mr-2" /> Add Stat
              </Button>
            </div>
          </div>
        );
        
      case 'values':
        return (
          <div className="space-y-4">
            <Label className="text-sm font-medium">Values</Label>
            <div className="space-y-3">
              {(Array.isArray(content) ? content : []).map((value, idx) => (
                <Card key={idx} className="p-3">
                  <div className="space-y-2">
                    <div className="grid grid-cols-3 gap-2">
                      <Input
                        placeholder="Title"
                        value={value.title || ''}
                        onChange={(e) => {
                          const newValues = [...content];
                          newValues[idx] = { ...newValues[idx], title: e.target.value };
                          onChange(newValues);
                        }}
                      />
                      <Input
                        placeholder="Icon (e.g., target, award)"
                        value={value.icon || ''}
                        onChange={(e) => {
                          const newValues = [...content];
                          newValues[idx] = { ...newValues[idx], icon: e.target.value };
                          onChange(newValues);
                        }}
                      />
                      <div className="flex gap-2">
                        <Input
                          placeholder="Color (e.g., cyan)"
                          value={value.color || ''}
                          onChange={(e) => {
                            const newValues = [...content];
                            newValues[idx] = { ...newValues[idx], color: e.target.value };
                            onChange(newValues);
                          }}
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const newValues = content.filter((_, i) => i !== idx);
                            onChange(newValues);
                          }}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                    <Textarea
                      placeholder="Description"
                      value={value.description || ''}
                      onChange={(e) => {
                        const newValues = [...content];
                        newValues[idx] = { ...newValues[idx], description: e.target.value };
                        onChange(newValues);
                      }}
                      rows={2}
                    />
                  </div>
                </Card>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const newValues = [...(Array.isArray(content) ? content : []), { title: '', description: '', icon: '', color: '' }];
                  onChange(newValues);
                }}
              >
                <Plus className="w-4 h-4 mr-2" /> Add Value
              </Button>
            </div>
          </div>
        );

      case 'timeline':
        return (
          <div className="space-y-4">
            <Label className="text-sm font-medium">Timeline Events</Label>
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {(Array.isArray(content) ? content : []).map((item, idx) => (
                <Card key={idx} className="p-3">
                  <div className="space-y-2">
                    <div className="grid grid-cols-4 gap-2">
                      <Input
                        placeholder="Date (e.g., Dec 2020)"
                        value={item.date || ''}
                        onChange={(e) => {
                          const newTimeline = [...content];
                          newTimeline[idx] = { ...newTimeline[idx], date: e.target.value };
                          onChange(newTimeline);
                        }}
                      />
                      <Input
                        placeholder="Year"
                        value={item.year || ''}
                        onChange={(e) => {
                          const newTimeline = [...content];
                          newTimeline[idx] = { ...newTimeline[idx], year: e.target.value };
                          onChange(newTimeline);
                        }}
                      />
                      <Input
                        placeholder="Highlight"
                        value={item.highlight || ''}
                        onChange={(e) => {
                          const newTimeline = [...content];
                          newTimeline[idx] = { ...newTimeline[idx], highlight: e.target.value };
                          onChange(newTimeline);
                        }}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const newTimeline = content.filter((_, i) => i !== idx);
                          onChange(newTimeline);
                        }}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                    <Input
                      placeholder="Title"
                      value={item.title || ''}
                      onChange={(e) => {
                        const newTimeline = [...content];
                        newTimeline[idx] = { ...newTimeline[idx], title: e.target.value };
                        onChange(newTimeline);
                      }}
                    />
                    <Input
                      placeholder="Description"
                      value={item.description || ''}
                      onChange={(e) => {
                        const newTimeline = [...content];
                        newTimeline[idx] = { ...newTimeline[idx], description: e.target.value };
                        onChange(newTimeline);
                      }}
                    />
                  </div>
                </Card>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const newTimeline = [...(Array.isArray(content) ? content : []), { date: '', year: '', title: '', description: '', highlight: '' }];
                  onChange(newTimeline);
                }}
              >
                <Plus className="w-4 h-4 mr-2" /> Add Timeline Event
              </Button>
            </div>
          </div>
        );
      
      case 'process_steps':
        return (
          <div className="space-y-4">
            <Label className="text-sm font-medium">Process Steps</Label>
            <p className="text-xs text-muted-foreground mb-2">
              Define the step-by-step process shown on the Services page.
            </p>
            <div className="space-y-3">
              {(Array.isArray(content) ? content : []).map((step, idx) => (
                <Card key={idx} className="p-3">
                  <div className="space-y-2">
                    <div className="grid grid-cols-4 gap-2">
                      <Input
                        placeholder="Step #"
                        value={step.step || ''}
                        onChange={(e) => {
                          const newSteps = [...content];
                          newSteps[idx] = { ...newSteps[idx], step: e.target.value };
                          onChange(newSteps);
                        }}
                        className="w-20"
                      />
                      <Input
                        placeholder="Title"
                        value={step.title || ''}
                        onChange={(e) => {
                          const newSteps = [...content];
                          newSteps[idx] = { ...newSteps[idx], title: e.target.value };
                          onChange(newSteps);
                        }}
                        className="col-span-2"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const newSteps = content.filter((_, i) => i !== idx);
                          onChange(newSteps);
                        }}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                    <Input
                      placeholder="Description"
                      value={step.desc || step.description || ''}
                      onChange={(e) => {
                        const newSteps = [...content];
                        newSteps[idx] = { ...newSteps[idx], desc: e.target.value };
                        onChange(newSteps);
                      }}
                    />
                  </div>
                </Card>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const newSteps = [...(Array.isArray(content) ? content : []), { step: String((content?.length || 0) + 1).padStart(2, '0'), title: '', desc: '' }];
                  onChange(newSteps);
                }}
              >
                <Plus className="w-4 h-4 mr-2" /> Add Step
              </Button>
            </div>
          </div>
        );
        
      case 'company_info':
        return (
          <div className="space-y-4">
            <FieldEditor
              label="Company Name"
              value={content.name}
              onChange={(v) => onChange({ ...content, name: v })}
            />
            <FieldEditor
              label="Tagline"
              value={content.tagline}
              onChange={(v) => onChange({ ...content, tagline: v })}
            />
            <FieldEditor
              label="Description"
              value={content.description}
              onChange={(v) => onChange({ ...content, description: v })}
              type="textarea"
            />
            <FieldEditor
              label="Phone"
              value={content.phone}
              onChange={(v) => onChange({ ...content, phone: v })}
            />
            <FieldEditor
              label="Email"
              value={content.email}
              onChange={(v) => onChange({ ...content, email: v })}
            />
            <FieldEditor
              label="Address"
              value={content.address}
              onChange={(v) => onChange({ ...content, address: v })}
              type="textarea"
            />
          </div>
        );
      
      case 'office':
        return (
          <div className="space-y-4">
            <FieldEditor
              label="Office Name"
              value={content.name}
              onChange={(v) => onChange({ ...content, name: v })}
              placeholder="e.g., Head Office, Gurgaon"
            />
            <FieldEditor
              label="Address"
              value={content.address}
              onChange={(v) => onChange({ ...content, address: v })}
              type="textarea"
              placeholder="Full office address"
            />
            <div className="grid grid-cols-2 gap-4">
              <FieldEditor
                label="Phone"
                value={content.phone}
                onChange={(v) => onChange({ ...content, phone: v })}
              />
              <FieldEditor
                label="Email"
                value={content.email}
                onChange={(v) => onChange({ ...content, email: v })}
              />
            </div>
            <FieldEditor
              label="Timing"
              value={content.timing}
              onChange={(v) => onChange({ ...content, timing: v })}
              placeholder="e.g., Mon-Sat: 10 AM - 7 PM"
            />
          </div>
        );
      
      case 'final_cta':
      case 'careers_cta':
        return (
          <div className="space-y-4">
            <FieldEditor
              label="Section Title"
              value={content.title}
              onChange={(v) => onChange({ ...content, title: v })}
            />
            <FieldEditor
              label="Subtitle / Description"
              value={content.subtitle}
              onChange={(v) => onChange({ ...content, subtitle: v })}
              type="textarea"
              rows={2}
            />
            <div className="grid grid-cols-2 gap-4">
              <FieldEditor
                label="Button Text"
                value={content.cta_text || content.cta_primary_text}
                onChange={(v) => onChange({ ...content, cta_text: v, cta_primary_text: v })}
              />
              <FieldEditor
                label="Button Link"
                value={content.cta_link || content.cta_primary_link}
                onChange={(v) => onChange({ ...content, cta_link: v, cta_primary_link: v })}
              />
            </div>
          </div>
        );
      
      case 'life_photos':
        return (
          <div className="space-y-4">
            <Label className="text-sm font-medium">Gallery Photos</Label>
            <p className="text-xs text-muted-foreground mb-4">
              Add image URLs for the "Life at UniXperts" gallery section.
            </p>
            <div className="space-y-3">
              {(Array.isArray(content) ? content : []).map((photo, idx) => {
                // Handle different photo formats: string URL, {url}, {image}, or object
                const photoUrl = typeof photo === 'string' 
                  ? photo 
                  : (photo?.url || photo?.image || '');
                const photoAlt = typeof photo === 'object' 
                  ? (photo?.alt || photo?.caption || '') 
                  : '';
                
                return (
                  <Card key={idx} className="p-3">
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <Input
                          placeholder="Image URL"
                          value={photoUrl}
                          onChange={(e) => {
                            const newPhotos = [...content];
                            newPhotos[idx] = typeof photo === 'string' 
                              ? e.target.value 
                              : { ...photo, url: e.target.value };
                            onChange(newPhotos);
                          }}
                          className="flex-1"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const newPhotos = content.filter((_, i) => i !== idx);
                            onChange(newPhotos);
                          }}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                      <Input
                        placeholder="Alt text / Caption"
                        value={photoAlt}
                        onChange={(e) => {
                          const newPhotos = [...content];
                          newPhotos[idx] = { 
                            url: photoUrl,
                            alt: e.target.value 
                          };
                          onChange(newPhotos);
                        }}
                      />
                      {photoUrl && (
                        <div className="mt-2">
                          <img 
                            src={photoUrl} 
                            alt={photoAlt || 'Preview'} 
                            className="h-20 w-auto rounded object-cover"
                            onError={(e) => e.target.style.display = 'none'}
                          />
                        </div>
                      )}
                    </div>
                  </Card>
                );
              })}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const newPhotos = [...(Array.isArray(content) ? content : []), { url: '', alt: '' }];
                  onChange(newPhotos);
                }}
              >
                <Plus className="w-4 h-4 mr-2" /> Add Photo
              </Button>
            </div>
          </div>
        );
      
      case 'branch_offices':
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Branch Offices</Label>
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    toast.info('Fetching latest Google ratings...');
                    const response = await fetch(`${API_URL}/api/places/ratings/all`);
                    const data = await response.json();
                    if (data.success && data.ratings) {
                      // Update each office with its Google rating
                      const updatedOffices = (Array.isArray(content) ? content : []).map(office => {
                        const match = data.ratings.find(r => 
                          r.name.toLowerCase().includes(office.city?.toLowerCase()) ||
                          office.city?.toLowerCase().includes(r.name.split(' ').pop().toLowerCase())
                        );
                        if (match && match.rating) {
                          return {
                            ...office,
                            rating: match.rating,
                            reviews: match.user_ratings_total,
                            googleMapsUrl: match.url || office.googleMapsUrl
                          };
                        }
                        return office;
                      });
                      onChange(updatedOffices);
                      toast.success(`Updated ratings for ${data.ratings.filter(r => r.rating).length} offices`);
                    }
                  } catch (error) {
                    toast.error('Failed to fetch Google ratings');
                    console.error(error);
                  }
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <RefreshCw className="w-4 h-4 mr-2" /> Auto-fetch Google Ratings
              </Button>
            </div>
            <p className="text-xs text-green-600 dark:text-green-400">
              Click "Auto-fetch Google Ratings" to pull live ratings from Google Places API for all offices.
            </p>
            <div className="space-y-3 max-h-[600px] overflow-y-auto">
              {(Array.isArray(content) ? content : []).map((office, idx) => (
                <Card key={idx} className="p-3 dark:bg-slate-800">
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        placeholder="City"
                        value={office.city || ''}
                        onChange={(e) => {
                          const newOffices = [...content];
                          newOffices[idx] = { ...newOffices[idx], city: e.target.value };
                          onChange(newOffices);
                        }}
                      />
                      <Input
                        placeholder="Region (e.g., North India)"
                        value={office.region || ''}
                        onChange={(e) => {
                          const newOffices = [...content];
                          newOffices[idx] = { ...newOffices[idx], region: e.target.value };
                          onChange(newOffices);
                        }}
                      />
                    </div>
                    <Textarea
                      placeholder="Address"
                      value={office.address || ''}
                      onChange={(e) => {
                        const newOffices = [...content];
                        newOffices[idx] = { ...newOffices[idx], address: e.target.value };
                        onChange(newOffices);
                      }}
                      rows={2}
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        placeholder="Phone"
                        value={office.phone || ''}
                        onChange={(e) => {
                          const newOffices = [...content];
                          newOffices[idx] = { ...newOffices[idx], phone: e.target.value };
                          onChange(newOffices);
                        }}
                      />
                      <Input
                        placeholder="Email (e.g., info.city@unixperts.com)"
                        value={office.email || ''}
                        onChange={(e) => {
                          const newOffices = [...content];
                          newOffices[idx] = { ...newOffices[idx], email: e.target.value };
                          onChange(newOffices);
                        }}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        placeholder="Google Rating (e.g., 4.8)"
                        value={office.rating || ''}
                        onChange={(e) => {
                          const newOffices = [...content];
                          newOffices[idx] = { ...newOffices[idx], rating: e.target.value };
                          onChange(newOffices);
                        }}
                      />
                      <Input
                        placeholder="Review Count (e.g., 156)"
                        value={office.reviews || ''}
                        onChange={(e) => {
                          const newOffices = [...content];
                          newOffices[idx] = { ...newOffices[idx], reviews: e.target.value };
                          onChange(newOffices);
                        }}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Google Maps URL (e.g., https://maps.google.com/...)"
                        value={office.googleMapsUrl || ''}
                        onChange={(e) => {
                          const newOffices = [...content];
                          newOffices[idx] = { ...newOffices[idx], googleMapsUrl: e.target.value };
                          onChange(newOffices);
                        }}
                        className="flex-1"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const newOffices = content.filter((_, i) => i !== idx);
                          onChange(newOffices);
                        }}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const newOffices = [...(Array.isArray(content) ? content : []), { city: '', region: '', address: '', phone: '', email: '', rating: '', reviews: '', googleMapsUrl: '' }];
                  onChange(newOffices);
                }}
              >
                <Plus className="w-4 h-4 mr-2" /> Add Office
              </Button>
            </div>
          </div>
        );
      
      case 'destinations':
        return (
          <div className="space-y-4">
            <Label className="text-sm font-medium">Destination Cards</Label>
            <p className="text-xs text-muted-foreground mb-2">
              Configure destination cards for the homepage. <strong>Drag to reorder</strong> - the first card appears as the hero (full width) on mobile.
            </p>
            <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg mb-4">
              <p className="text-xs text-blue-700 dark:text-blue-300">
                <strong>Mobile Bento Layout:</strong> 1st = Hero (full width), 2nd-3rd = Row, 4th = Wide (full width), 5th-6th = Row
              </p>
            </div>
            <div className="space-y-3">
              {(Array.isArray(content) ? content : []).map((dest, idx) => (
                <Card key={idx} className={`p-4 ${idx === 0 ? 'ring-2 ring-ux-cyan' : ''}`}>
                  <div className="space-y-3">
                    {/* Order controls and position indicator */}
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-bold px-2 py-1 rounded ${
                          idx === 0 ? 'bg-ux-cyan text-white' : 
                          idx === 3 ? 'bg-violet-500 text-white' : 
                          'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300'
                        }`}>
                          {idx === 0 ? '🌟 HERO' : idx === 3 ? '📐 WIDE' : `#${idx + 1}`}
                        </span>
                        <span className="text-sm font-medium">{dest.country || 'New Destination'}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={idx === 0}
                          onClick={() => {
                            const newDests = [...content];
                            [newDests[idx - 1], newDests[idx]] = [newDests[idx], newDests[idx - 1]];
                            onChange(newDests);
                          }}
                          title="Move up"
                        >
                          <ChevronUp className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={idx === content.length - 1}
                          onClick={() => {
                            const newDests = [...content];
                            [newDests[idx], newDests[idx + 1]] = [newDests[idx + 1], newDests[idx]];
                            onChange(newDests);
                          }}
                          title="Move down"
                        >
                          <ChevronDown className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const newDests = content.filter((_, i) => i !== idx);
                            onChange(newDests);
                          }}
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                    
                    {/* Form fields */}
                    <div className="grid grid-cols-4 gap-2">
                      <Input
                        placeholder="Country"
                        value={dest.country || ''}
                        onChange={(e) => {
                          const newDests = [...content];
                          newDests[idx] = { ...newDests[idx], country: e.target.value };
                          onChange(newDests);
                        }}
                      />
                      <Input
                        placeholder="Slug (e.g., usa)"
                        value={dest.slug || ''}
                        onChange={(e) => {
                          const newDests = [...content];
                          newDests[idx] = { ...newDests[idx], slug: e.target.value };
                          onChange(newDests);
                        }}
                      />
                      <Input
                        placeholder="Flag Emoji"
                        value={dest.flag || ''}
                        onChange={(e) => {
                          const newDests = [...content];
                          newDests[idx] = { ...newDests[idx], flag: e.target.value };
                          onChange(newDests);
                        }}
                      />
                      <Input
                        placeholder="Universities"
                        value={dest.universities || ''}
                        onChange={(e) => {
                          const newDests = [...content];
                          newDests[idx] = { ...newDests[idx], universities: e.target.value };
                          onChange(newDests);
                        }}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        placeholder="Card Image URL"
                        value={dest.image_url || ''}
                        onChange={(e) => {
                          const newDests = [...content];
                          newDests[idx] = { ...newDests[idx], image_url: e.target.value };
                          onChange(newDests);
                        }}
                      />
                      <Input
                        placeholder="Tagline (e.g., Land of Opportunities)"
                        value={dest.tagline || ''}
                        onChange={(e) => {
                          const newDests = [...content];
                          newDests[idx] = { ...newDests[idx], tagline: e.target.value };
                          onChange(newDests);
                        }}
                      />
                    </div>
                    {dest.image_url && (
                      <div className="mt-2">
                        <img 
                          src={dest.image_url} 
                          alt={dest.country} 
                          className="h-20 w-32 object-cover rounded-lg border"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      </div>
                    )}
                  </div>
                </Card>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const newDests = [...(Array.isArray(content) ? content : []), { country: '', slug: '', flag: '', universities: '', image_url: '', tagline: '' }];
                  onChange(newDests);
                }}
              >
                <Plus className="w-4 h-4 mr-2" /> Add Destination
              </Button>
            </div>
          </div>
        );
      
      case 'social_links':
        return (
          <div className="space-y-4">
            <Label className="text-sm font-medium">Social Media Links</Label>
            <p className="text-xs text-muted-foreground mb-2">
              Configure links to your social media profiles.
            </p>
            <div className="space-y-3">
              {(Array.isArray(content) ? content : []).map((link, idx) => (
                <Card key={idx} className="p-3">
                  <div className="grid grid-cols-4 gap-2">
                    <Input
                      placeholder="Platform (e.g., facebook)"
                      value={link.platform || ''}
                      onChange={(e) => {
                        const newLinks = [...content];
                        newLinks[idx] = { ...newLinks[idx], platform: e.target.value };
                        onChange(newLinks);
                      }}
                    />
                    <Input
                      placeholder="Label (e.g., Facebook)"
                      value={link.label || ''}
                      onChange={(e) => {
                        const newLinks = [...content];
                        newLinks[idx] = { ...newLinks[idx], label: e.target.value };
                        onChange(newLinks);
                      }}
                    />
                    <Input
                      placeholder="URL"
                      value={link.url || ''}
                      onChange={(e) => {
                        const newLinks = [...content];
                        newLinks[idx] = { ...newLinks[idx], url: e.target.value };
                        onChange(newLinks);
                      }}
                      className="col-span-1"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const newLinks = content.filter((_, i) => i !== idx);
                        onChange(newLinks);
                      }}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </Card>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const newLinks = [...(Array.isArray(content) ? content : []), { platform: '', label: '', url: '' }];
                  onChange(newLinks);
                }}
              >
                <Plus className="w-4 h-4 mr-2" /> Add Social Link
              </Button>
            </div>
          </div>
        );

      case 'footer_links':
        return (
          <div className="space-y-4">
            <Label className="text-sm font-medium">Footer Links</Label>
            <p className="text-xs text-muted-foreground mb-2">
              Organize footer links by category.
            </p>
            
            {/* Quick Links */}
            <Card className="p-3">
              <Label className="text-sm font-medium mb-2 block">Quick Links</Label>
              <div className="space-y-2">
                {(content?.quick_links || []).map((link, idx) => (
                  <div key={idx} className="grid grid-cols-3 gap-2">
                    <Input
                      placeholder="Label"
                      value={link.label || ''}
                      onChange={(e) => {
                        const newQuickLinks = [...(content?.quick_links || [])];
                        newQuickLinks[idx] = { ...newQuickLinks[idx], label: e.target.value };
                        onChange({ ...content, quick_links: newQuickLinks });
                      }}
                    />
                    <Input
                      placeholder="URL"
                      value={link.url || ''}
                      onChange={(e) => {
                        const newQuickLinks = [...(content?.quick_links || [])];
                        newQuickLinks[idx] = { ...newQuickLinks[idx], url: e.target.value };
                        onChange({ ...content, quick_links: newQuickLinks });
                      }}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const newQuickLinks = (content?.quick_links || []).filter((_, i) => i !== idx);
                        onChange({ ...content, quick_links: newQuickLinks });
                      }}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const newQuickLinks = [...(content?.quick_links || []), { label: '', url: '' }];
                    onChange({ ...content, quick_links: newQuickLinks });
                  }}
                >
                  <Plus className="w-4 h-4 mr-1" /> Add Link
                </Button>
              </div>
            </Card>

            {/* Destinations */}
            <Card className="p-3">
              <Label className="text-sm font-medium mb-2 block">Destination Links</Label>
              <div className="space-y-2">
                {(content?.destinations || []).map((link, idx) => (
                  <div key={idx} className="grid grid-cols-3 gap-2">
                    <Input
                      placeholder="Label"
                      value={link.label || ''}
                      onChange={(e) => {
                        const newDests = [...(content?.destinations || [])];
                        newDests[idx] = { ...newDests[idx], label: e.target.value };
                        onChange({ ...content, destinations: newDests });
                      }}
                    />
                    <Input
                      placeholder="URL"
                      value={link.url || ''}
                      onChange={(e) => {
                        const newDests = [...(content?.destinations || [])];
                        newDests[idx] = { ...newDests[idx], url: e.target.value };
                        onChange({ ...content, destinations: newDests });
                      }}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const newDests = (content?.destinations || []).filter((_, i) => i !== idx);
                        onChange({ ...content, destinations: newDests });
                      }}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const newDests = [...(content?.destinations || []), { label: '', url: '' }];
                    onChange({ ...content, destinations: newDests });
                  }}
                >
                  <Plus className="w-4 h-4 mr-1" /> Add Link
                </Button>
              </div>
            </Card>

            {/* Resources */}
            <Card className="p-3">
              <Label className="text-sm font-medium mb-2 block">Resource Links</Label>
              <div className="space-y-2">
                {(content?.resources || []).map((link, idx) => (
                  <div key={idx} className="grid grid-cols-3 gap-2">
                    <Input
                      placeholder="Label"
                      value={link.label || ''}
                      onChange={(e) => {
                        const newRes = [...(content?.resources || [])];
                        newRes[idx] = { ...newRes[idx], label: e.target.value };
                        onChange({ ...content, resources: newRes });
                      }}
                    />
                    <Input
                      placeholder="URL"
                      value={link.url || ''}
                      onChange={(e) => {
                        const newRes = [...(content?.resources || [])];
                        newRes[idx] = { ...newRes[idx], url: e.target.value };
                        onChange({ ...content, resources: newRes });
                      }}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const newRes = (content?.resources || []).filter((_, i) => i !== idx);
                        onChange({ ...content, resources: newRes });
                      }}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const newRes = [...(content?.resources || []), { label: '', url: '' }];
                    onChange({ ...content, resources: newRes });
                  }}
                >
                  <Plus className="w-4 h-4 mr-1" /> Add Link
                </Button>
              </div>
            </Card>

            {/* Legal */}
            <Card className="p-3">
              <Label className="text-sm font-medium mb-2 block">Legal Links</Label>
              <div className="space-y-2">
                {(content?.legal || []).map((link, idx) => (
                  <div key={idx} className="grid grid-cols-3 gap-2">
                    <Input
                      placeholder="Label"
                      value={link.label || ''}
                      onChange={(e) => {
                        const newLegal = [...(content?.legal || [])];
                        newLegal[idx] = { ...newLegal[idx], label: e.target.value };
                        onChange({ ...content, legal: newLegal });
                      }}
                    />
                    <Input
                      placeholder="URL"
                      value={link.url || ''}
                      onChange={(e) => {
                        const newLegal = [...(content?.legal || [])];
                        newLegal[idx] = { ...newLegal[idx], url: e.target.value };
                        onChange({ ...content, legal: newLegal });
                      }}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const newLegal = (content?.legal || []).filter((_, i) => i !== idx);
                        onChange({ ...content, legal: newLegal });
                      }}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const newLegal = [...(content?.legal || []), { label: '', url: '' }];
                    onChange({ ...content, legal: newLegal });
                  }}
                >
                  <Plus className="w-4 h-4 mr-1" /> Add Link
                </Button>
              </div>
            </Card>
          </div>
        );

      case 'gptw_badge':
        return (
          <div className="space-y-4">
            <Label className="text-sm font-medium">Great Place to Work Badge</Label>
            <p className="text-xs text-muted-foreground mb-2">
              Configure the GPTW certification badge shown in the footer.
            </p>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="gptw_enabled"
                  checked={content?.enabled || false}
                  onChange={(e) => onChange({ ...content, enabled: e.target.checked })}
                  className="h-4 w-4"
                />
                <Label htmlFor="gptw_enabled">Show GPTW Badge</Label>
              </div>
              <Input
                placeholder="Badge Title (e.g., Great Place to Work)"
                value={content?.title || ''}
                onChange={(e) => onChange({ ...content, title: e.target.value })}
              />
              <Input
                placeholder="Subtitle (e.g., Certified Organization)"
                value={content?.subtitle || ''}
                onChange={(e) => onChange({ ...content, subtitle: e.target.value })}
              />
              <Input
                placeholder="Badge Image URL"
                value={content?.badge_url || ''}
                onChange={(e) => onChange({ ...content, badge_url: e.target.value })}
              />
              <Input
                placeholder="Link URL (e.g., https://www.greatplacetowork.in/)"
                value={content?.link || ''}
                onChange={(e) => onChange({ ...content, link: e.target.value })}
              />
            </div>
          </div>
        );

      case 'section_header':
      case 'featured_header':
      case 'all_stories_header':
      case 'leadership_header':
      case 'values_header':
      case 'culture_header':
      case 'past_header':
      case 'trending_header':
        return (
          <div className="space-y-4">
            <Label className="text-sm font-medium">Section Header</Label>
            <p className="text-xs text-muted-foreground mb-2">
              Configure the header text for this page section.
            </p>
            <Input
              placeholder="Badge Text"
              value={content?.badge || ''}
              onChange={(e) => onChange({ ...content, badge: e.target.value })}
            />
            <Input
              placeholder="Title"
              value={content?.title || ''}
              onChange={(e) => onChange({ ...content, title: e.target.value })}
            />
            <Input
              placeholder="Title Highlight (gradient text)"
              value={content?.title_highlight || ''}
              onChange={(e) => onChange({ ...content, title_highlight: e.target.value })}
            />
            <Textarea
              placeholder="Subtitle / Description"
              value={content?.subtitle || ''}
              onChange={(e) => onChange({ ...content, subtitle: e.target.value })}
              rows={2}
            />
          </div>
        );

      case 'services':
        return (
          <div className="space-y-4">
            <Label className="text-sm font-medium">Services List</Label>
            <p className="text-xs text-muted-foreground mb-2">
              Configure the services shown on the Services page.
            </p>
            <div className="space-y-3 max-h-[500px] overflow-y-auto">
              {(Array.isArray(content) ? content : []).map((service, idx) => (
                <Card key={idx} className="p-3">
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-sm">{service.title || 'New Service'}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const newServices = content.filter((_, i) => i !== idx);
                          onChange(newServices);
                        }}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <Input
                        placeholder="ID (e.g., visa-assistance)"
                        value={service.id || ''}
                        onChange={(e) => {
                          const newServices = [...content];
                          newServices[idx] = { ...newServices[idx], id: e.target.value };
                          onChange(newServices);
                        }}
                      />
                      <Input
                        placeholder="Title"
                        value={service.title || ''}
                        onChange={(e) => {
                          const newServices = [...content];
                          newServices[idx] = { ...newServices[idx], title: e.target.value };
                          onChange(newServices);
                        }}
                      />
                      <Input
                        placeholder="Icon (e.g., plane)"
                        value={service.icon || ''}
                        onChange={(e) => {
                          const newServices = [...content];
                          newServices[idx] = { ...newServices[idx], icon: e.target.value };
                          onChange(newServices);
                        }}
                      />
                    </div>
                    <Textarea
                      placeholder="Short Description"
                      value={service.short_description || ''}
                      onChange={(e) => {
                        const newServices = [...content];
                        newServices[idx] = { ...newServices[idx], short_description: e.target.value };
                        onChange(newServices);
                      }}
                      rows={2}
                    />
                    <Input
                      placeholder="Features (comma-separated)"
                      value={Array.isArray(service.features) ? service.features.join(', ') : ''}
                      onChange={(e) => {
                        const newServices = [...content];
                        newServices[idx] = { ...newServices[idx], features: e.target.value.split(',').map(f => f.trim()) };
                        onChange(newServices);
                      }}
                    />
                    <Input
                      placeholder="Color (e.g., cyan, violet)"
                      value={service.color || ''}
                      onChange={(e) => {
                        const newServices = [...content];
                        newServices[idx] = { ...newServices[idx], color: e.target.value };
                        onChange(newServices);
                      }}
                    />
                  </div>
                </Card>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const newServices = [...(Array.isArray(content) ? content : []), { 
                    id: '', title: '', icon: '', short_description: '', features: [], color: '' 
                  }];
                  onChange(newServices);
                }}
              >
                <Plus className="w-4 h-4 mr-2" /> Add Service
              </Button>
            </div>
          </div>
        );

      case 'why_choose_us':
        return (
          <div className="space-y-4">
            <Label className="text-sm font-medium">Why Choose Us</Label>
            <p className="text-xs text-muted-foreground mb-2">
              Configure the "Why Choose Us" feature cards.
            </p>
            <div className="space-y-3">
              {(Array.isArray(content) ? content : []).map((item, idx) => (
                <Card key={idx} className="p-3">
                  <div className="space-y-2">
                    <div className="grid grid-cols-3 gap-2">
                      <Input
                        placeholder="Icon (e.g., award)"
                        value={item.icon || ''}
                        onChange={(e) => {
                          const newItems = [...content];
                          newItems[idx] = { ...newItems[idx], icon: e.target.value };
                          onChange(newItems);
                        }}
                      />
                      <Input
                        placeholder="Title"
                        value={item.title || ''}
                        onChange={(e) => {
                          const newItems = [...content];
                          newItems[idx] = { ...newItems[idx], title: e.target.value };
                          onChange(newItems);
                        }}
                        className="col-span-2"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Description"
                        value={item.description || ''}
                        onChange={(e) => {
                          const newItems = [...content];
                          newItems[idx] = { ...newItems[idx], description: e.target.value };
                          onChange(newItems);
                        }}
                        className="flex-1"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const newItems = content.filter((_, i) => i !== idx);
                          onChange(newItems);
                        }}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const newItems = [...(Array.isArray(content) ? content : []), { icon: '', title: '', description: '' }];
                  onChange(newItems);
                }}
              >
                <Plus className="w-4 h-4 mr-2" /> Add Item
              </Button>
            </div>
          </div>
        );
        
      case 'cta_labels':
        return (
          <div className="space-y-4">
            <Label className="text-sm font-medium">Global CTA Labels</Label>
            <p className="text-xs text-muted-foreground mb-2">
              Reusable button labels and CTA copy used across the website (homepage, footer, etc.).
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <FieldEditor
                label="Find Dream University (Hero CTA)"
                value={content?.find_dream_university}
                onChange={(v) => onChange({ ...content, find_dream_university: v })}
                placeholder="Find My Dream University"
              />
              <FieldEditor
                label="Create Free Account"
                value={content?.create_account}
                onChange={(v) => onChange({ ...content, create_account: v })}
                placeholder="Create Free Account"
              />
              <FieldEditor
                label="Book Free Consultation"
                value={content?.book_consultation}
                onChange={(v) => onChange({ ...content, book_consultation: v })}
                placeholder="Book Free Consultation"
              />
              <FieldEditor
                label="Talk to an Expert"
                value={content?.talk_to_expert}
                onChange={(v) => onChange({ ...content, talk_to_expert: v })}
                placeholder="Talk to an Expert"
              />
              <FieldEditor
                label="Get Free Consultation"
                value={content?.get_free_consultation}
                onChange={(v) => onChange({ ...content, get_free_consultation: v })}
                placeholder="Get Free Consultation"
              />
              <FieldEditor
                label="View All Programs"
                value={content?.view_all_programs}
                onChange={(v) => onChange({ ...content, view_all_programs: v })}
                placeholder="View All Programs"
              />
            </div>
            <div className="pt-4 border-t">
              <Label className="text-sm font-medium mb-2 block">Footer CTA Strip</Label>
              <div className="space-y-3">
                <FieldEditor
                  label="Footer CTA Title"
                  value={content?.footer_cta_title}
                  onChange={(v) => onChange({ ...content, footer_cta_title: v })}
                  placeholder="Ready to Start Your Journey?"
                />
                <FieldEditor
                  label="Footer CTA Subtitle"
                  value={content?.footer_cta_subtitle}
                  onChange={(v) => onChange({ ...content, footer_cta_subtitle: v })}
                  type="textarea"
                  rows={2}
                  placeholder="Join 30,000+ students who found their dream university with UniXperts"
                />
                <FieldEditor
                  label="Footer CTA Button Text"
                  value={content?.footer_cta_button}
                  onChange={(v) => onChange({ ...content, footer_cta_button: v })}
                  placeholder="Book Free Consultation"
                />
              </div>
            </div>
          </div>
        );

      case 'section_headers':
        return (
          <div className="space-y-4">
            <Label className="text-sm font-medium">Homepage Section Headers</Label>
            <p className="text-xs text-muted-foreground mb-2">
              Configure the badge / title / subtitle for each major homepage section.
            </p>
            {[
              { key: 'destinations', label: 'Destinations Section' },
              { key: 'testimonials', label: 'Testimonials Section' }
            ].map(({ key, label }) => (
              <Card key={key} className="p-3">
                <Label className="text-sm font-medium mb-2 block">{label}</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    placeholder="Badge"
                    value={content?.[key]?.badge || ''}
                    onChange={(e) => onChange({
                      ...content,
                      [key]: { ...(content?.[key] || {}), badge: e.target.value }
                    })}
                  />
                  <Input
                    placeholder="Link Text (optional)"
                    value={content?.[key]?.link_text || ''}
                    onChange={(e) => onChange({
                      ...content,
                      [key]: { ...(content?.[key] || {}), link_text: e.target.value }
                    })}
                  />
                  <Input
                    placeholder="Title"
                    value={content?.[key]?.title || ''}
                    onChange={(e) => onChange({
                      ...content,
                      [key]: { ...(content?.[key] || {}), title: e.target.value }
                    })}
                  />
                  <Input
                    placeholder="Title Highlight (gradient)"
                    value={content?.[key]?.title_highlight || ''}
                    onChange={(e) => onChange({
                      ...content,
                      [key]: { ...(content?.[key] || {}), title_highlight: e.target.value }
                    })}
                  />
                </div>
              </Card>
            ))}
          </div>
        );

      case 'hero_stat_cards':
        return (
          <div className="space-y-4">
            <Label className="text-sm font-medium">Hero Stat Cards</Label>
            <p className="text-xs text-muted-foreground mb-2">
              Glassmorphic stat cards shown next to the hero on desktop. First card is featured (full width).
            </p>
            <div className="space-y-3">
              {(Array.isArray(content) ? content : []).map((card, idx) => (
                <Card key={idx} className="p-3">
                  <div className="grid grid-cols-4 gap-2 items-start">
                    <Input
                      placeholder="Icon (e.g., GraduationCap)"
                      value={card.icon || ''}
                      onChange={(e) => {
                        const newCards = [...content];
                        newCards[idx] = { ...newCards[idx], icon: e.target.value };
                        onChange(newCards);
                      }}
                    />
                    <Input
                      placeholder="Value (e.g., 500+)"
                      value={card.value || ''}
                      onChange={(e) => {
                        const newCards = [...content];
                        newCards[idx] = { ...newCards[idx], value: e.target.value };
                        onChange(newCards);
                      }}
                    />
                    <Input
                      placeholder="Label"
                      value={card.label || ''}
                      onChange={(e) => {
                        const newCards = [...content];
                        newCards[idx] = { ...newCards[idx], label: e.target.value };
                        onChange(newCards);
                      }}
                    />
                    <div className="flex gap-2">
                      <Input
                        placeholder="Sublabel (optional)"
                        value={card.sublabel || ''}
                        onChange={(e) => {
                          const newCards = [...content];
                          newCards[idx] = { ...newCards[idx], sublabel: e.target.value };
                          onChange(newCards);
                        }}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const newCards = content.filter((_, i) => i !== idx);
                          onChange(newCards);
                        }}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const newCards = [...(Array.isArray(content) ? content : []), { icon: '', value: '', label: '', sublabel: '' }];
                  onChange(newCards);
                }}
              >
                <Plus className="w-4 h-4 mr-2" /> Add Stat Card
              </Button>
            </div>
          </div>
        );

      case 'student_services_list':
        return (
          <div className="space-y-4">
            <Label className="text-sm font-medium">Student Services</Label>
            <p className="text-xs text-muted-foreground mb-2">
              Configure the 4 service cards (Education Loan, Forex, Housing, Insurance).
              Use slug as routing key (e.g., "education-loan", "forex", "housing", "insurance").
            </p>
            <div className="space-y-3 max-h-[600px] overflow-y-auto">
              {(Array.isArray(content) ? content : []).map((svc, idx) => (
                <Card key={idx} className="p-3">
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-sm">{svc.title || 'New Service'}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onChange(content.filter((_, i) => i !== idx))}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        placeholder="Slug (e.g., education-loan)"
                        value={svc.slug || ''}
                        onChange={(e) => {
                          const arr = [...content]; arr[idx] = { ...arr[idx], slug: e.target.value }; onChange(arr);
                        }}
                      />
                      <Input
                        placeholder="Title"
                        value={svc.title || ''}
                        onChange={(e) => {
                          const arr = [...content]; arr[idx] = { ...arr[idx], title: e.target.value }; onChange(arr);
                        }}
                      />
                      <Input
                        placeholder="Tagline (e.g., Fund Your Dreams)"
                        value={svc.tagline || ''}
                        onChange={(e) => {
                          const arr = [...content]; arr[idx] = { ...arr[idx], tagline: e.target.value }; onChange(arr);
                        }}
                      />
                      <Input
                        placeholder="Icon (Banknote, RefreshCw, Home, Shield)"
                        value={svc.icon || ''}
                        onChange={(e) => {
                          const arr = [...content]; arr[idx] = { ...arr[idx], icon: e.target.value }; onChange(arr);
                        }}
                      />
                      <Input
                        placeholder="Color gradient (e.g., from-emerald-500 to-teal-600)"
                        value={svc.color || ''}
                        onChange={(e) => {
                          const arr = [...content]; arr[idx] = { ...arr[idx], color: e.target.value }; onChange(arr);
                        }}
                        className="col-span-2"
                      />
                    </div>
                    <Textarea
                      placeholder="Description"
                      value={svc.description || ''}
                      rows={2}
                      onChange={(e) => {
                        const arr = [...content]; arr[idx] = { ...arr[idx], description: e.target.value }; onChange(arr);
                      }}
                    />
                    <Textarea
                      placeholder="Features (one per line)"
                      value={Array.isArray(svc.features) ? svc.features.join('\n') : ''}
                      rows={3}
                      onChange={(e) => {
                        const arr = [...content]; arr[idx] = { ...arr[idx], features: e.target.value.split('\n').filter(Boolean) }; onChange(arr);
                      }}
                    />
                    <Input
                      placeholder="Partners (comma-separated)"
                      value={Array.isArray(svc.partners) ? svc.partners.join(', ') : ''}
                      onChange={(e) => {
                        const arr = [...content]; arr[idx] = { ...arr[idx], partners: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }; onChange(arr);
                      }}
                    />
                    <Input
                      placeholder="CTA Button Text (e.g., Check Loan Eligibility)"
                      value={svc.cta || ''}
                      onChange={(e) => {
                        const arr = [...content]; arr[idx] = { ...arr[idx], cta: e.target.value }; onChange(arr);
                      }}
                    />
                  </div>
                </Card>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const arr = [...(Array.isArray(content) ? content : []), {
                    slug: '', title: '', tagline: '', icon: '', color: '', description: '', features: [], partners: [], cta: ''
                  }];
                  onChange(arr);
                }}
              >
                <Plus className="w-4 h-4 mr-2" /> Add Service
              </Button>
            </div>
          </div>
        );

      case 'help_cta':
        return (
          <div className="space-y-4">
            <Label className="text-sm font-medium">Help CTA Strip</Label>
            <p className="text-xs text-muted-foreground mb-2">
              The "Need Help Choosing?" strip near the bottom of the Student Services overview.
            </p>
            <FieldEditor
              label="Title"
              value={content?.title}
              onChange={(v) => onChange({ ...content, title: v })}
              placeholder="Need Help Choosing?"
            />
            <FieldEditor
              label="Subtitle"
              value={content?.subtitle}
              onChange={(v) => onChange({ ...content, subtitle: v })}
              type="textarea"
              rows={2}
            />
            <div className="grid grid-cols-2 gap-2">
              <FieldEditor
                label="Call Button Text"
                value={content?.call_label}
                onChange={(v) => onChange({ ...content, call_label: v })}
                placeholder="Call Us Now"
              />
              <FieldEditor
                label="WhatsApp Button Text"
                value={content?.whatsapp_label}
                onChange={(v) => onChange({ ...content, whatsapp_label: v })}
                placeholder="WhatsApp"
              />
            </div>
          </div>
        );

      case 'gptw_banner':
        return (
          <div className="space-y-4">
            <Label className="text-sm font-medium">GPTW Banner (Life @ UniXperts section)</Label>
            <FieldEditor
              label="Title"
              value={content?.title}
              onChange={(v) => onChange({ ...content, title: v })}
              placeholder="Certified Great Place to Work"
            />
            <FieldEditor
              label="Subtitle"
              value={content?.subtitle}
              onChange={(v) => onChange({ ...content, subtitle: v })}
              type="textarea"
              rows={2}
              placeholder="Recognized for our exceptional workplace culture - September 2025 to September 2026"
            />
            <div className="grid grid-cols-2 gap-2">
              <FieldEditor
                label="CTA Button Text"
                value={content?.cta_text}
                onChange={(v) => onChange({ ...content, cta_text: v })}
                placeholder="Join Our Team"
              />
              <FieldEditor
                label="CTA Link (mailto/url)"
                value={content?.cta_link}
                onChange={(v) => onChange({ ...content, cta_link: v })}
                placeholder="mailto:hr@unixperts.com"
              />
            </div>
          </div>
        );

      case 'empty_state':
        return (
          <div className="space-y-4">
            <Label className="text-sm font-medium">Empty / No-Results State</Label>
            <FieldEditor
              label="No Results Title"
              value={content?.title}
              onChange={(v) => onChange({ ...content, title: v })}
              placeholder="No programs found"
            />
            <FieldEditor
              label="No Results Subtitle"
              value={content?.subtitle}
              onChange={(v) => onChange({ ...content, subtitle: v })}
              type="textarea"
              rows={2}
              placeholder="Try adjusting your filters or search term"
            />
            <FieldEditor
              label="Clear Filters Button"
              value={content?.clear_label}
              onChange={(v) => onChange({ ...content, clear_label: v })}
              placeholder="Clear All Filters"
            />
          </div>
        );

      case 'template_strings':
        return (
          <div className="space-y-4">
            <Label className="text-sm font-medium">Page Template Strings</Label>
            <p className="text-xs text-muted-foreground mb-2">Editable microcopy used across this page template.</p>
            <FieldEditor label="Apply Now" value={content?.apply_now} onChange={(v) => onChange({ ...content, apply_now: v })} placeholder="Apply Now" />
            <FieldEditor label="Requirements Header" value={content?.requirements_header} onChange={(v) => onChange({ ...content, requirements_header: v })} placeholder="Eligibility & Requirements" />
            <FieldEditor label="Tuition Header" value={content?.tuition_header} onChange={(v) => onChange({ ...content, tuition_header: v })} placeholder="Tuition Fees" />
            <FieldEditor label="Duration Label" value={content?.duration_label} onChange={(v) => onChange({ ...content, duration_label: v })} placeholder="Duration" />
            <FieldEditor label="Intake Label" value={content?.intake_label} onChange={(v) => onChange({ ...content, intake_label: v })} placeholder="Intake" />
            <FieldEditor label="Related Programs Header" value={content?.related_header} onChange={(v) => onChange({ ...content, related_header: v })} placeholder="Related Programs" />
          </div>
        );

      case 'destination_hero':
        return (
          <div className="space-y-4">
            <Label className="text-sm font-medium">Destination Hero</Label>
            <p className="text-xs text-muted-foreground mb-2">Country name, flag, hero image and tagline shown at top of the destination page.</p>
            <div className="grid grid-cols-2 gap-3">
              <FieldEditor label="Name" value={content?.name} onChange={(v) => onChange({ ...content, name: v })} placeholder="USA" />
              <FieldEditor label="Full Name" value={content?.fullName} onChange={(v) => onChange({ ...content, fullName: v })} placeholder="United States of America" />
              <FieldEditor label="Flag (emoji)" value={content?.flag} onChange={(v) => onChange({ ...content, flag: v })} placeholder="🇺🇸" />
              <FieldEditor label="Tagline" value={content?.tagline} onChange={(v) => onChange({ ...content, tagline: v })} placeholder="Land of Opportunities" />
            </div>
            <FieldEditor label="Description" value={content?.description} onChange={(v) => onChange({ ...content, description: v })} type="textarea" rows={3} />
            <FieldEditor label="Hero Image URL (desktop)" value={content?.heroImage} onChange={(v) => onChange({ ...content, heroImage: v })} placeholder="https://..." />
            <FieldEditor label="Hero Image URL (mobile)" value={content?.heroImageMobile} onChange={(v) => onChange({ ...content, heroImageMobile: v })} placeholder="https://..." />
          </div>
        );

      case 'destination_stats':
        return (
          <div className="space-y-4">
            <Label className="text-sm font-medium">Destination Stats Bar</Label>
            <div className="grid grid-cols-2 gap-3">
              <FieldEditor label="Universities" value={content?.universities} onChange={(v) => onChange({ ...content, universities: v })} placeholder="4,000+" />
              <FieldEditor label="International Students" value={content?.internationalStudents} onChange={(v) => onChange({ ...content, internationalStudents: v })} placeholder="1M+" />
              <FieldEditor label="Avg Tuition" value={content?.avgTuition} onChange={(v) => onChange({ ...content, avgTuition: v })} placeholder="$25K-$60K" />
              <FieldEditor label="Work Visa Period" value={content?.workVisa} onChange={(v) => onChange({ ...content, workVisa: v })} placeholder="3 Years OPT" />
              <FieldEditor label="Intakes (comma-sep)" value={Array.isArray(content?.intakes) ? content.intakes.join(', ') : content?.intakes} onChange={(v) => onChange({ ...content, intakes: typeof v === 'string' ? v.split(',').map(s => s.trim()).filter(Boolean) : v })} placeholder="Fall, Spring, Summer" />
            </div>
          </div>
        );

      case 'destination_costs':
        return (
          <div className="space-y-4">
            <Label className="text-sm font-medium">Cost Summary</Label>
            <div className="grid grid-cols-2 gap-3">
              <FieldEditor label="Tuition Range" value={content?.tuitionRange} onChange={(v) => onChange({ ...content, tuitionRange: v })} placeholder="$25,000 - $60,000/year" />
              <FieldEditor label="Living Cost" value={content?.livingCost} onChange={(v) => onChange({ ...content, livingCost: v })} placeholder="$12,000 - $20,000/year" />
              <FieldEditor label="Total Annual" value={content?.totalAnnual} onChange={(v) => onChange({ ...content, totalAnnual: v })} placeholder="$37K - $80K/year" />
            </div>
          </div>
        );

      case 'destination_requirements':
        return (
          <div className="space-y-4">
            <Label className="text-sm font-medium">Admission Requirements</Label>
            <FieldEditor label="Academic" value={content?.academic} onChange={(v) => onChange({ ...content, academic: v })} placeholder="GPA 3.0+ / 75%+" />
            <FieldEditor label="Tests (comma-sep)" value={Array.isArray(content?.tests) ? content.tests.join(', ') : content?.tests} onChange={(v) => onChange({ ...content, tests: typeof v === 'string' ? v.split(',').map(s => s.trim()).filter(Boolean) : v })} placeholder="GRE, GMAT, TOEFL, IELTS" />
            <FieldEditor label="Documents (comma-sep)" value={Array.isArray(content?.documents) ? content.documents.join(', ') : content?.documents} onChange={(v) => onChange({ ...content, documents: typeof v === 'string' ? v.split(',').map(s => s.trim()).filter(Boolean) : v })} placeholder="SOP, LOR, Transcripts, Resume" />
          </div>
        );

      case 'whyStudy':
        return (
          <div className="space-y-3">
            <Label className="text-sm font-medium">Why Study Here (cards)</Label>
            {(Array.isArray(content) ? content : []).map((card, idx) => (
              <Card key={idx} className="p-3 space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">{card.title || `Card ${idx + 1}`}</span>
                  <Button variant="ghost" size="sm" onClick={() => onChange(content.filter((_, i) => i !== idx))}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
                <Input placeholder="Title" value={card.title || ''} onChange={(e) => { const arr = [...content]; arr[idx] = { ...arr[idx], title: e.target.value }; onChange(arr); }} />
                <Textarea placeholder="Description" rows={2} value={card.description || ''} onChange={(e) => { const arr = [...content]; arr[idx] = { ...arr[idx], description: e.target.value }; onChange(arr); }} />
              </Card>
            ))}
            <Button variant="outline" size="sm" onClick={() => onChange([...(Array.isArray(content) ? content : []), { title: '', description: '' }])}>
              <Plus className="w-4 h-4 mr-2" /> Add Card
            </Button>
          </div>
        );

      case 'popularCourses':
        return (
          <div className="space-y-3">
            <Label className="text-sm font-medium">Popular Courses</Label>
            <Textarea
              placeholder="One per line"
              rows={8}
              value={Array.isArray(content) ? content.join('\n') : ''}
              onChange={(e) => onChange(e.target.value.split('\n').filter(s => s.trim()))}
            />
          </div>
        );

      case 'scholarships':
        return (
          <div className="space-y-3">
            <Label className="text-sm font-medium">Scholarships</Label>
            {(Array.isArray(content) ? content : []).map((s, idx) => (
              <Card key={idx} className="p-3 space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">{s.name || `Scholarship ${idx + 1}`}</span>
                  <Button variant="ghost" size="sm" onClick={() => onChange(content.filter((_, i) => i !== idx))}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
                <Input placeholder="Name" value={s.name || ''} onChange={(e) => { const arr = [...content]; arr[idx] = { ...arr[idx], name: e.target.value }; onChange(arr); }} />
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Coverage" value={s.coverage || ''} onChange={(e) => { const arr = [...content]; arr[idx] = { ...arr[idx], coverage: e.target.value }; onChange(arr); }} />
                  <Input placeholder="Eligibility" value={s.eligibility || ''} onChange={(e) => { const arr = [...content]; arr[idx] = { ...arr[idx], eligibility: e.target.value }; onChange(arr); }} />
                </div>
              </Card>
            ))}
            <Button variant="outline" size="sm" onClick={() => onChange([...(Array.isArray(content) ? content : []), { name: '', coverage: '', eligibility: '' }])}>
              <Plus className="w-4 h-4 mr-2" /> Add Scholarship
            </Button>
          </div>
        );

      case 'newsletter_cta':
        return (
          <div className="space-y-4">
            <Label className="text-sm font-medium">Newsletter / Subscribe CTA</Label>
            <p className="text-xs text-muted-foreground mb-2">Strip with email input + Subscribe button shown on Events / Blog pages.</p>
            <FieldEditor label="Title" value={content?.title} onChange={(v) => onChange({ ...content, title: v })} placeholder="Never Miss an Event" />
            <FieldEditor label="Subtitle" value={content?.subtitle} onChange={(v) => onChange({ ...content, subtitle: v })} type="textarea" rows={2} />
            <div className="grid grid-cols-2 gap-2">
              <FieldEditor label="Email Placeholder" value={content?.email_placeholder} onChange={(v) => onChange({ ...content, email_placeholder: v })} placeholder="Enter your email" />
              <FieldEditor label="Subscribe Button Text" value={content?.subscribe_label} onChange={(v) => onChange({ ...content, subscribe_label: v })} placeholder="Subscribe" />
            </div>
          </div>
        );

      case 'tags_card':
        return (
          <div className="space-y-4">
            <Label className="text-sm font-medium">Sidebar Tags Card (Blog list)</Label>
            <FieldEditor label="Title" value={content?.title} onChange={(v) => onChange({ ...content, title: v })} placeholder="Popular Tags" />
            <FieldEditor
              label="Tags (comma-separated)"
              value={Array.isArray(content?.tags) ? content.tags.join(', ') : (content?.tags || '')}
              onChange={(v) => onChange({ ...content, tags: typeof v === 'string' ? v.split(',').map(s => s.trim()).filter(Boolean) : v })}
              placeholder="USA, UK, Canada, Scholarships, Visa, IELTS, SOP, MBA, Engineering"
            />
          </div>
        );

      case 'intakes':
        return (
          <div className="space-y-3">
            <Label className="text-sm font-medium">Intake Cycles</Label>
            <p className="text-xs text-muted-foreground">Each cycle: name, start/end month, and a steps[] timeline.</p>
            {(Array.isArray(content) ? content : []).map((cycle, idx) => (
              <Card key={idx} className="p-3 space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">{cycle.name || `Cycle ${idx + 1}`}</span>
                  <Button variant="ghost" size="sm" onClick={() => onChange(content.filter((_, i) => i !== idx))}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <Input placeholder="Name (e.g., Fall 2025)" value={cycle.name || ''} onChange={(e) => { const arr = [...content]; arr[idx] = { ...arr[idx], name: e.target.value }; onChange(arr); }} />
                  <Input placeholder="Start Month" value={cycle.startMonth || ''} onChange={(e) => { const arr = [...content]; arr[idx] = { ...arr[idx], startMonth: e.target.value }; onChange(arr); }} />
                  <Input placeholder="End Month" value={cycle.endMonth || ''} onChange={(e) => { const arr = [...content]; arr[idx] = { ...arr[idx], endMonth: e.target.value }; onChange(arr); }} />
                </div>
                <Label className="text-xs text-muted-foreground">Steps (month / activity / details)</Label>
                {(Array.isArray(cycle.steps) ? cycle.steps : []).map((step, sIdx) => (
                  <div key={sIdx} className="grid grid-cols-12 gap-2 items-start">
                    <Input className="col-span-2" placeholder="Month" value={step.month || ''} onChange={(e) => {
                      const arr = [...content]; const steps = [...(arr[idx].steps || [])]; steps[sIdx] = { ...steps[sIdx], month: e.target.value };
                      arr[idx] = { ...arr[idx], steps }; onChange(arr);
                    }} />
                    <Input className="col-span-3" placeholder="Activity" value={step.activity || ''} onChange={(e) => {
                      const arr = [...content]; const steps = [...(arr[idx].steps || [])]; steps[sIdx] = { ...steps[sIdx], activity: e.target.value };
                      arr[idx] = { ...arr[idx], steps }; onChange(arr);
                    }} />
                    <Input className="col-span-6" placeholder="Details" value={step.details || ''} onChange={(e) => {
                      const arr = [...content]; const steps = [...(arr[idx].steps || [])]; steps[sIdx] = { ...steps[sIdx], details: e.target.value };
                      arr[idx] = { ...arr[idx], steps }; onChange(arr);
                    }} />
                    <Button className="col-span-1" variant="ghost" size="sm" onClick={() => {
                      const arr = [...content]; const steps = (arr[idx].steps || []).filter((_, i) => i !== sIdx);
                      arr[idx] = { ...arr[idx], steps }; onChange(arr);
                    }}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => {
                  const arr = [...content]; const steps = [...(arr[idx].steps || []), { month: '', activity: '', details: '' }];
                  arr[idx] = { ...arr[idx], steps }; onChange(arr);
                }}>
                  <Plus className="w-3 h-3 mr-1" /> Add Step
                </Button>
              </Card>
            ))}
            <Button variant="outline" size="sm" onClick={() => onChange([...(Array.isArray(content) ? content : []), { name: '', startMonth: '', endMonth: '', steps: [] }])}>
              <Plus className="w-4 h-4 mr-2" /> Add Intake Cycle
            </Button>
          </div>
        );

      case 'topUniversities':
        return (
          <div className="space-y-3">
            <Label className="text-sm font-medium">Top Universities</Label>
            <p className="text-xs text-muted-foreground">Universities featured in the destination comparison tool.</p>
            <div className="max-h-[700px] overflow-y-auto space-y-3">
              {(Array.isArray(content) ? content : []).map((uni, idx) => (
                <Card key={idx} className="p-3 space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">{uni.name || `University ${idx + 1}`}</span>
                    <Button variant="ghost" size="sm" onClick={() => onChange(content.filter((_, i) => i !== idx))}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input placeholder="ID (numeric)" value={uni.id ?? ''} onChange={(e) => { const arr = [...content]; arr[idx] = { ...arr[idx], id: Number(e.target.value) || e.target.value }; onChange(arr); }} />
                    <Input placeholder="Short Name (e.g., MIT)" value={uni.shortName || ''} onChange={(e) => { const arr = [...content]; arr[idx] = { ...arr[idx], shortName: e.target.value }; onChange(arr); }} />
                  </div>
                  <Input placeholder="Full Name" value={uni.name || ''} onChange={(e) => { const arr = [...content]; arr[idx] = { ...arr[idx], name: e.target.value }; onChange(arr); }} />
                  <div className="grid grid-cols-3 gap-2">
                    <Input placeholder="Rank (#1)" value={uni.rank || ''} onChange={(e) => { const arr = [...content]; arr[idx] = { ...arr[idx], rank: e.target.value }; onChange(arr); }} />
                    <Input placeholder="Location" value={uni.location || ''} onChange={(e) => { const arr = [...content]; arr[idx] = { ...arr[idx], location: e.target.value }; onChange(arr); }} />
                    <Input placeholder="Acceptance Rate" value={uni.acceptanceRate || ''} onChange={(e) => { const arr = [...content]; arr[idx] = { ...arr[idx], acceptanceRate: e.target.value }; onChange(arr); }} />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <Input placeholder="Tuition" value={uni.tuition || ''} onChange={(e) => { const arr = [...content]; arr[idx] = { ...arr[idx], tuition: e.target.value }; onChange(arr); }} />
                    <Input placeholder="Avg Salary" value={uni.avgSalary || ''} onChange={(e) => { const arr = [...content]; arr[idx] = { ...arr[idx], avgSalary: e.target.value }; onChange(arr); }} />
                    <Input placeholder="Duration" value={uni.duration || ''} onChange={(e) => { const arr = [...content]; arr[idx] = { ...arr[idx], duration: e.target.value }; onChange(arr); }} />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input placeholder="Student Count" value={uni.studentCount || ''} onChange={(e) => { const arr = [...content]; arr[idx] = { ...arr[idx], studentCount: e.target.value }; onChange(arr); }} />
                    <Input placeholder="Intl Students %" value={uni.intlStudents || ''} onChange={(e) => { const arr = [...content]; arr[idx] = { ...arr[idx], intlStudents: e.target.value }; onChange(arr); }} />
                  </div>
                  <Input placeholder="Programs (comma-separated)" value={Array.isArray(uni.programs) ? uni.programs.join(', ') : (uni.programs || '')} onChange={(e) => {
                    const arr = [...content]; arr[idx] = { ...arr[idx], programs: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }; onChange(arr);
                  }} />
                </Card>
              ))}
              <Button variant="outline" size="sm" onClick={() => onChange([...(Array.isArray(content) ? content : []), { id: Date.now(), name: '', shortName: '', rank: '', location: '', programs: [], tuition: '', acceptanceRate: '', avgSalary: '', studentCount: '', intlStudents: '', duration: '' }])}>
                <Plus className="w-4 h-4 mr-2" /> Add University
              </Button>
            </div>
          </div>
        );

      default:
        // Generic JSON editor for other sections
        return (
          <div className="space-y-2">
            <Label className="text-sm font-medium">Raw Content (JSON)</Label>
            <Textarea
              value={JSON.stringify(content, null, 2)}
              onChange={(e) => {
                try {
                  onChange(JSON.parse(e.target.value));
                } catch (err) {
                  // Invalid JSON - don't update
                }
              }}
              rows={10}
              className="font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground">
              Edit the JSON directly. Invalid JSON will not be saved.
            </p>
          </div>
        );
    }
  };
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-medium">{SECTION_LABELS[sectionKey] || sectionKey}</h4>
      </div>
      {renderEditor()}
    </div>
  );
};

export { SectionEditor };
export default SectionEditor;
